import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { fetchNarrativeTemplates } from "../Redux/feasibility";
import { sfqApi } from "../../../api/feasibility";
import NarrativeEditor from "../FeasibilityEditor/NarrativeEditor";
import TemplateListRail from "../TemplateListRail";
import {
  TEMPLATE_NAME_MAX, templateNameError, templateErrorMessage,
} from "../templateName";
import { PERM } from "../../../utils/permissions";
import { hasAnyPermission, hasPermission } from "../../../utils/hasPermission";

const SAVE_DEBOUNCE_MS = 2000;

const Wrap = styled.div`
  display: flex;
  gap: 0;
  align-items: flex-start;
`;

const Pane = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 860px;
  padding-left: 20px;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const NameInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 5px 10px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  color: #212529;
  background: ${(p) => (p.readOnly ? "#f1f3f5" : "#fff")};
  outline: none;
  &:focus { border-color: #0d6efd; }
`;

const SaveStatus = styled.span`
  font-size: 12px;
  white-space: nowrap;
  color: ${(p) => (p.$err ? "#dc3545" : "#6c757d")};
`;

const Btn = styled.button`
  padding: 5px 12px;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  border: 1px solid ${(p) => (p.$danger ? "#dc3545" : "#ced4da")};
  background: #fff;
  color: ${(p) => (p.$danger ? "#dc3545" : "#495057")};
  &:hover:not(:disabled) { filter: brightness(0.96); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Hint = styled.div`
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 12px;
`;

const Placeholder = styled.div`
  padding: 60px 0;
  text-align: center;
  color: #adb5bd;
  font-size: 13px;
`;

export default function NarrativeTemplateManager() {
  const dispatch = useDispatch();
  const { narrativeTemplates, ntLoading } = useSelector((s) => s.feasibility);
  const permissions = useSelector((s) => s.userInfo.permissions);
  // 템플릿 쓰기(기본 템플릿 포함)는 백엔드 라우트가 SFQ_STUDY_EDIT 또는 SFQ_ADMIN을
  // 요구한다. 화면도 같은 조합으로 건다 — 탭을 여는 조건과도 동일하다.
  const canWriteTpl = hasAnyPermission(permissions, [PERM.SFQ_STUDY_EDIT, PERM.SFQ_ADMIN]);
  const canCreateUser = hasPermission(permissions, PERM.SFQ_STUDY_EDIT);

  const [selectedId, setSelectedId] = useState(null);
  const [tpl, setTpl]         = useState(null);   // the selected template's full row
  // 이름 입력칸의 편집 중인 값. tpl.name은 마지막으로 저장된 이름으로 남겨 둔다 —
  // 한 값을 같이 쓰면 타이핑이 tpl.name까지 바꿔 버려 blur 때 "바뀐 게 없다"가 되고
  // 이름 저장이 통째로 안 나갔다.
  const [nameDraft, setNameDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [saving, setSaving]   = useState(false);
  const timerRef = useRef(null);
  // 디바운스 대기 중인 편집분 — { id, html }. 마지막 타이핑 후 SAVE_DEBOUNCE_MS 안에
  // 탭을 옮기거나 다른 템플릿을 열면 타이머가 그대로 죽어 변경분이 사라지므로,
  // 화면을 떠날 때 여기 남은 걸 밀어 넣는다. 어느 템플릿의 편집분인지도 같이 들고
  // 있어야 템플릿을 바꾼 뒤에도 올바른 id로 저장된다.
  const pendingRef = useRef(null);

  // 언마운트 이후에도 요청 자체는 살아서 끝난다 — 상태는 건드리지 않는다.
  const flushPending = useCallback(() => {
    clearTimeout(timerRef.current);
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    sfqApi.updateNarrativeTemplate(pending.id, { html: pending.html }).catch(() => {});
  }, []);

  useEffect(() => {
    dispatch(fetchNarrativeTemplates());
  }, [dispatch]);

  // 언마운트(탭 이동·페이지 이탈) 때의 flush는 별도 effect로 둔다 — 목록 조회 쪽에
  // 얹으면 그 effect가 다시 돌 때마다 저장이 딸려 나간다.
  useEffect(() => flushPending, [flushPending]);

  // 브라우저 새로고침·탭 닫기는 flush를 기다려 줄 수 없다. 저장 대기분이 있을 때만
  // 확인창을 띄워 사용자가 남아 있기로 하면 디바운스 타이머가 마저 돈다.
  useEffect(() => {
    const handler = (e) => {
      if (!pendingRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  // Load the selected template's html. Nothing is cached in Redux — the list carries
  // metadata only, so a stale body can never be shown.
  useEffect(() => {
    if (!selectedId) { setTpl(null); setNameDraft(""); return; }
    let cancelled = false;
    setLoading(true);
    sfqApi.getNarrativeTemplate(selectedId)
      .then((res) => {
        if (cancelled) return;
        setTpl(res.data.data);
        setNameDraft(res.data.data.name || "");
      })
      .catch(() => { if (!cancelled) { setTpl(null); setNameDraft(""); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; flushPending(); };
  }, [selectedId, flushPending]);

  // 기본 템플릿도 이제 편집자·관리자 모두 고칠 수 있다. 목록에는 기본 템플릿과 내
  // 템플릿만 내려오므로, 쓰기 권한이 있으면 열려 있는 템플릿은 전부 편집 가능하다.
  const editable = !!tpl && canWriteTpl;

  const save = useCallback(async (body) => {
    setSaving(true);
    try {
      await sfqApi.updateNarrativeTemplate(selectedId, body);
      // 저장에 성공한 html이 아직 대기분과 같을 때만 비운다 — 요청이 도는 동안 더
      // 타이핑했다면 그 편집분은 대기 상태로 남아 있어야 한다.
      if (pendingRef.current?.html === body.html) pendingRef.current = null;
      setSaveMsg("Saved ✓");
      setTimeout(() => setSaveMsg(""), 2000);
      if (body.name !== undefined) dispatch(fetchNarrativeTemplates());
      return true;
    } catch (err) {
      // 실패분은 대기 상태로 남겨 둔다. 화면을 떠날 때 한 번 더 시도된다.
      setSaveMsg(templateErrorMessage(err, "저장하지 못했습니다."));
      return false;
    } finally {
      setSaving(false);
    }
  }, [selectedId, dispatch]);

  // 이름은 blur(또는 Enter)에서 커밋한다. 저장된 이름과 다를 때만 요청을 보내고,
  // 빈 이름이나 저장 실패는 서버가 들고 있는 이름으로 입력칸을 되돌린다.
  async function commitName() {
    if (!editable || !tpl) return;
    const name = nameDraft.trim();
    if (!name || name === tpl.name) { setNameDraft(tpl.name || ""); return; }
    const invalid = templateNameError(name);
    if (invalid) { setSaveMsg(invalid); return; }   // 고칠 수 있게 입력값은 그대로 둔다
    if (await save({ name })) {
      setTpl((p) => (p ? { ...p, name } : p));
      setNameDraft(name);
    } else {
      setNameDraft(tpl.name || "");
    }
  }

  function handleHtmlChange(html) {
    setSaveMsg("");
    setTpl((p) => (p ? { ...p, html } : p));
    pendingRef.current = { id: selectedId, html };
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => save({ html }), SAVE_DEBOUNCE_MS);
  }

  async function handleCreate(scope) {
    const name = window.prompt(
      scope === "default" ? "새 기본 템플릿 이름" : "새 템플릿 이름"
    );
    if (name === null) return;               // 취소
    const invalid = templateNameError(name);
    if (invalid) { if (name.trim()) alert(invalid); return; }
    try {
      const res = await sfqApi.createNarrativeTemplate({ name: name.trim(), scope, html: "" });
      await dispatch(fetchNarrativeTemplates()).unwrap();
      setSelectedId(res.data.data.id);
    } catch (err) {
      alert("생성 실패: " + templateErrorMessage(err, "잠시 후 다시 시도해 주세요."));
    }
  }

  async function handleDelete() {
    if (!window.confirm(`"${tpl.name}" 템플릿을 삭제하시겠습니까?`)) return;
    // 지울 행에 저장 대기분을 남겨 두면 선택 해제 시 flush가 404를 부른다.
    clearTimeout(timerRef.current);
    pendingRef.current = null;
    try {
      await sfqApi.deleteNarrativeTemplate(selectedId);
      setSelectedId(null);
      dispatch(fetchNarrativeTemplates());
    } catch (err) {
      alert("삭제 실패: " + templateErrorMessage(err, "잠시 후 다시 시도해 주세요."));
    }
  }

  // Read-only default template → give the user a private copy they can edit.
  async function handleClone() {
    const name = window.prompt("복제할 템플릿 이름", `${tpl.name} (복사본)`);
    if (name === null) return;               // 취소
    const invalid = templateNameError(name);
    if (invalid) { if (name.trim()) alert(invalid); return; }
    try {
      const res = await sfqApi.createNarrativeTemplate({
        name: name.trim(), scope: "user", html: tpl.html || "",
      });
      await dispatch(fetchNarrativeTemplates()).unwrap();
      setSelectedId(res.data.data.id);
    } catch (err) {
      alert("복제 실패: " + templateErrorMessage(err, "잠시 후 다시 시도해 주세요."));
    }
  }

  return (
    <Wrap>
      <TemplateListRail
        templates={narrativeTemplates}
        loading={ntLoading}
        selectedId={selectedId}
        onSelect={setSelectedId}
        onCreate={handleCreate}
        canCreateDefault={canWriteTpl}
        canCreateUser={canCreateUser}
      />

      <Pane>
        {!selectedId ? (
          <Placeholder>왼쪽에서 템플릿을 선택하거나 새로 만드세요.</Placeholder>
        ) : loading || !tpl ? (
          <Placeholder>Loading template...</Placeholder>
        ) : (
          <>
            <TopRow>
              <NameInput
                value={nameDraft}
                readOnly={!editable}
                maxLength={TEMPLATE_NAME_MAX}
                aria-label="템플릿 이름"
                onChange={(e) => setNameDraft(e.target.value)}
                onBlur={commitName}
                onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              />
              {saving && <SaveStatus>Saving...</SaveStatus>}
              {!saving && saveMsg && (
                <SaveStatus $err={saveMsg !== "Saved ✓"}>{saveMsg}</SaveStatus>
              )}
              {/* 기본 템플릿을 직접 고치는 대신 내 사본으로 가져갈 때 쓴다. */}
              {canWriteTpl && <Btn onClick={handleClone}>내 템플릿으로 복제</Btn>}
              {editable && <Btn $danger onClick={handleDelete}>삭제</Btn>}
            </TopRow>

            <Hint>
              {!editable
                ? "이 템플릿은 조회만 가능합니다."
                : tpl.scope === "default"
                  ? "기본 템플릿은 모든 사용자가 함께 쓰는 템플릿입니다. 변경 사항은 자동 저장되며 즉시 전체에 반영됩니다."
                  : "SFQ의 Study Overview에서 이 템플릿을 불러와 적용할 수 있습니다. 변경 사항은 자동 저장됩니다."}
            </Hint>

            {/* Remount per template so the editor initialises with that template's html. */}
            <NarrativeEditor
              key={tpl.id}
              value={tpl.html || ""}
              onChange={handleHtmlChange}
              readOnly={!editable}
              sfqId={`template-${tpl.id}`}
            />
          </>
        )}
      </Pane>
    </Wrap>
  );
}
